import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useFilterProvider } from 'src/context/FilterProvider'
import { numberWithCommas } from 'src/utils'
import { toLocationDisplayName } from 'src/utils/locationLabel'
import ga from 'src/utils/pixels/gtag'
import { pushToDataLayer } from 'src/utils/pixels/spotmeEvents'
import { SpaceType } from '../SearchBar/types'
import {
  NearbySearchCountsResult,
  SPACE_TYPE_TO_ES_LOCATION_TYPE,
  useNearbySearchCounts
} from './hostCtaQuery'

type HostCTAProps = {
  /** Pre-fetched data from getStaticProps — when provided, skips client-side query */
  nearbySearchData?: NearbySearchCountsResult
  /** Location name for display (city or hood name) */
  locationName?: string
}

/**
 * Editorial host-pivot card shown below the renter lead form on empty/sparse
 * search results. Recolored from the legacy green-status palette to the
 * neutral editorial vocabulary (cream-300 + ink hairline + blue-500 accent +
 * italic Fraunces numeral) so it reads as the final beat in a harmonized
 * three-card stack (OnDemand → CaptureLead → HostCTA), not as a separate
 * "success status" island.
 *
 * Status colors are prohibited as decoration on marketing surfaces — the
 * green chrome conflicted with that rule and broke the card's role as a
 * footer beat in the editorial rhythm.
 */
function HostCTA({ nearbySearchData, locationName }: HostCTAProps) {
  const { t } = useTranslation('host_cta')
  const { filters } = useFilterProvider()

  // Derive locationType from the first spaceType filter (kept for the
  // analytics params + space-type label below; the shared hook re-derives it
  // internally for the query variables).
  const spaceType = filters.spaceTypes?.[0] as SpaceType | undefined
  const locationType = spaceType
    ? SPACE_TYPE_TO_ES_LOCATION_TYPE[spaceType]
    : undefined

  // Shared with <CaptureLead> — static data from getStaticProps when
  // provided, identical self-query otherwise (Apollo dedupes in-flight).
  const result = useNearbySearchCounts(nearbySearchData)
  if (!result || result.total === 0) return null

  // Reverse-map slugs like "san-pedro-garza-garcia" back to their accented
  // display name ("San Pedro Garza García"); already-human names pass through.
  const location =
    toLocationDisplayName(locationName ?? filters.description) ?? 'tu zona'

  const handleCtaClick = (
    ctaType: 'individual' | 'business' | 'traditional'
  ) => {
    const params = {
      cta_type: ctaType,
      city: location,
      space_type: locationType,
      nearby_count: result?.total ?? 0,
      page_type: 'search' as const
    }
    ga.event('host_cta_click', params)
    pushToDataLayer('spotme_host_cta_click', params)
  }

  // Explicit map for locationType → translated space type name (no dynamic t() keys)
  const spaceTypeLabels: Record<string, string> = {
    'self-storage': t('SPACE_TYPE.self-storage'),
    parking: t('SPACE_TYPE.parking'),
    warehouse: t('SPACE_TYPE.warehouse'),
    'industrial-warehouse': t('SPACE_TYPE.industrial-warehouse')
  }

  // Two-rendering split:
  //   - count >= 10 → italic Fraunces numeral as visual focal point + body
  //     text with the count factored out (STAT_TIERN_BODY keys).
  //   - count < 10 → single line with no numeral (STAT_LOW), too low to
  //     justify a hero number.
  const count = result.total
  const showNumeral = count >= 10
  // numberWithCommas is hydration-safe (deterministic across Node/Chrome) —
  // never reach for toLocaleString here. Falls back to bare String() when
  // numberWithCommas returns undefined (null/NaN inputs), though count is
  // always a number from the GraphQL result.
  const formattedCount = numberWithCommas(count) ?? String(count)
  const numeralLabel = result.tier === 3 ? `${formattedCount}+` : formattedCount

  let statBody: string
  if (!showNumeral) {
    statBody = t('STAT_LOW', { location })
  } else if (result.tier === 1 && result.locationTypeFiltered && locationType) {
    statBody = t('STAT_TIER1_BODY', {
      spaceType: spaceTypeLabels[locationType] ?? '',
      location
    })
  } else if (result.tier <= 2) {
    statBody = t('STAT_TIER2_BODY', { location })
  } else {
    statBody = t('STAT_TIER3_BODY', { location })
  }

  const primaryCtaClass =
    'inline-flex items-center justify-center rounded-full bg-blue-500 px-5 py-2.5 text-sm font-medium text-white-500 transition-colors hover:bg-blue-600'
  const secondaryCtaClass =
    'inline-flex items-center justify-center rounded-full border border-ink-100/40 px-5 py-2.5 text-sm font-medium text-ink-700 transition-colors hover:border-ink-100/60 hover:bg-white-500/60'

  return (
    <section className="rounded-2xl border border-ink-100/15 bg-cream-300 p-5 md:p-8">
      <p className="flex items-center gap-2 font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
        <i
          aria-hidden="true"
          className="si si-badge-dollar text-[15px] leading-none md:text-[16px]"
        />
        {t('EYEBROW')}
      </p>
      <h3 className="mt-3 font-display text-xl leading-tight font-medium text-ink-700 md:text-[24px]">
        {t('TITLE')}
      </h3>
      <p className="mt-2 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
        {t('SUBTITLE')}
      </p>

      {showNumeral ? (
        <div className="mt-6 border-t border-ink-100/15 pt-5">
          <p className="font-display text-[44px] leading-none font-medium tracking-[-0.02em] text-blue-500 italic md:text-[60px]">
            {numeralLabel}
          </p>
          <p className="mt-3 text-[14px] leading-relaxed text-ink-200">
            {statBody}
          </p>
        </div>
      ) : (
        <p className="mt-5 border-t border-ink-100/15 pt-5 text-[14px] leading-relaxed text-ink-200">
          {statBody}
        </p>
      )}

      <p className="mt-3 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
        {t('PITCH')}
      </p>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <Link
          href="/anfitrion"
          className={primaryCtaClass}
          onClick={() => handleCtaClick('individual')}
        >
          {t('CTA_HOST')}
        </Link>
        <Link
          href="/anfitrion/negocio"
          className={secondaryCtaClass}
          onClick={() => handleCtaClick('business')}
        >
          {t('CTA_BUSINESS')}
        </Link>
        <Link
          href="/anfitrion/tradicional"
          className={secondaryCtaClass}
          onClick={() => handleCtaClick('traditional')}
        >
          {t('CTA_TRADITIONAL')}
        </Link>
      </div>
    </section>
  )
}

export default HostCTA
